import {
  fetchAll,
  findOneByField,
  createDocWithId,
  updateDocById,
} from "./firestoreHelpers";
import { normalizeNombre, legacyNormalizeNombre } from "../utils/nombre";

// Clave de dedup de locadores/locatarios: el nombre completo normalizado.
export const claveDedup = (data) => normalizeNombre(data?.apellido, data?.nombre);
export const claveDedupLegacy = (data) => legacyNormalizeNombre(data?.apellido, data?.nombre);

// Busca un registro existente con el mismo nombre, en tres pasadas:
//   1. query por la clave actual (el caso normal, una sola lectura);
//   2. query por la clave vieja, para registros guardados antes de que la clave
//      sacara acentos y ordenara las palabras;
//   3. barrido completo de la colección, para registros que quedaron sin
//      `nombreNormalizado` y que ninguna query puede encontrar.
// El barrido solo corre cuando las dos queries fallan (o sea, al dar de alta a
// alguien nuevo). Las colecciones son chicas, y es preferible esa lectura extra
// a terminar con dos fichas del mismo propietario.
export const findPersonaByNombre = async (coll, data) => {
  const key = claveDedup(data);
  if (!key) return null;

  const porClaveActual = await findOneByField(coll, "nombreNormalizado", key);
  if (porClaveActual) return porClaveActual;

  const legacyKey = claveDedupLegacy(data);
  if (legacyKey && legacyKey !== key) {
    const porClaveVieja = await findOneByField(coll, "nombreNormalizado", legacyKey);
    if (porClaveVieja) return porClaveVieja;
  }

  const todos = await fetchAll(coll);
  return todos.find((p) => claveDedup(p) === key) || null;
};

// Alta o merge. Si ya existe alguien con el mismo nombre, completa los campos
// que le falten y devuelve su id; nunca crea un segundo registro.
// Siempre reescribe `nombreNormalizado` con la clave actual, así los registros
// viejos quedan migrados a medida que se los toca.
export const savePersona = async (coll, data) => {
  const key = claveDedup(data);
  const existing = await findPersonaByNombre(coll, data);

  if (existing) {
    const merged = { ...existing };
    let changed = false;
    for (const [k, v] of Object.entries(data)) {
      if (v !== undefined && v !== "" && !existing[k]) {
        merged[k] = v;
        changed = true;
      }
    }
    if (existing.nombreNormalizado !== key) {
      merged.nombreNormalizado = key;
      changed = true;
    }
    if (changed) await updateDocById(coll, existing.idFirebase, merged);
    return existing.idFirebase;
  }

  return createDocWithId(coll, { ...data, nombreNormalizado: key });
};
